/**
 * Pure helpers for the per-user Country / Industry view (country-context.tsx):
 * the order of the Country options, the label of a region, the header chip text
 * and where the chip links to.
 *
 * No React, no path aliases: Node's test runner (`npm run test:unit`) loads it.
 */

import { canManageAdmin } from './roles.ts'

export const ALL = 'ALL'

export interface ViewSelectionLike {
  country: string
  industry: string
}

/** The fields of a `GET /api/me/view` country option these helpers read. */
export interface CountryOptionLike {
  code: string
  name: string | null
  currency: string | null
  /** `region` for a pack that covers several countries (Europe, `EU`). */
  kind?: string
  members?: string[]
}

export function isRegion(option: CountryOptionLike | undefined | null): boolean {
  return option?.kind === 'region'
}

/**
 * Country codes in the order the select lists them: All, then regions (Europe),
 * then single countries as the server sent them. The saved selection is kept
 * even when it is no longer an option, so the select never shows a blank value.
 */
export function orderedCountryCodes(countries: readonly CountryOptionLike[], selected: string): string[] {
  const regions = countries.filter(isRegion).map((c) => c.code)
  const single = countries.filter((c) => !isRegion(c)).map((c) => c.code)
  const codes = [ALL, ...regions, ...single]
  if (selected && !codes.includes(selected)) codes.push(selected)
  return codes
}

/** The option text in the select: a region reads "Europe (all)", a country its own label. */
export function countryOptionText(label: string, option: CountryOptionLike | undefined | null): string {
  return isRegion(option) ? `${label} (all)` : label
}

/**
 * The header chip text, or `null` for the All / All view (no chip then).
 * e.g. "India · INR", "Europe · EUR", "Japan · JPY · Manufacturing", "Cement".
 */
export function viewChipLabel(
  view: ViewSelectionLike,
  parts: { country: string; currency: string | null; industry: string }
): string | null {
  const out: string[] = []
  if (view.country !== ALL) {
    out.push(parts.country)
    if (parts.currency) out.push(parts.currency)
  }
  if (view.industry !== ALL) out.push(parts.industry)
  return out.length ? out.join(' · ') : null
}

/** Where the view is changed: Admin Settings for admin / AP-Manager, else the personal Settings page. */
export function viewSettingsHref(roles: readonly string[] | null | undefined): string {
  return canManageAdmin(roles) ? '/admin/settings' : '/settings'
}

export function sameView(a: ViewSelectionLike, b: ViewSelectionLike): boolean {
  return a.country === b.country && a.industry === b.industry
}
